import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';

export interface UserIdentity {
  id: string;
  user_id: string;
  identity_data: {
    email?: string;
    email_verified?: boolean;
    name?: string;
    picture?: string;
    provider_id?: string;
    sub?: string;
  };
  provider: string;
  last_sign_in_at: string;
  created_at: string;
  updated_at: string;
}

export function useUserIdentities() {
  const { user, getUserIdentities, linkGoogleAccount, unlinkIdentity } = useAuth();
  const { toast } = useToast();
  const [identities, setIdentities] = useState<UserIdentity[]>([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState<string | null>(null);

  const fetchIdentities = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const { data, error } = await getUserIdentities();
      
      if (error) {
        console.error('Error fetching identities:', error);
        toast({
          title: "Error loading linked accounts",
          description: error.message,
          variant: "destructive"
        });
        return;
      }
      
      setIdentities(data || []);
    } catch (error) {
      console.error('Error in fetchIdentities:', error);
    } finally {
      setLoading(false);
    }
  }, [user, getUserIdentities, toast]);

  useEffect(() => {
    fetchIdentities();
  }, [fetchIdentities]);

  const linkNewGoogleAccount = async () => {
    setLinking(true);
    
    try {
      // Store linking state in sessionStorage to persist across redirect
      sessionStorage.setItem('linking_google_account', 'true');
      console.log('[useUserIdentities] Starting Google account linking...');
      
      const { error } = await linkGoogleAccount('/profile');
      
      if (error) {
        console.error('[useUserIdentities] Link account error:', error);
        sessionStorage.removeItem('linking_google_account');
        
        // Provide more specific error messages
        let errorMessage = error.message;
        if (error.message.includes('already_linked')) {
          errorMessage = "This Google account is already linked to another user.";
        } else if (error.message.includes('same_identity')) {
          errorMessage = "This Google account is already linked to your profile.";
        } else if (error.message.includes('manual_linking_disabled')) {
          errorMessage = "Manual linking is not enabled. Please contact support.";
        }
        
        toast({
          title: "Failed to link account",
          description: errorMessage,
          variant: "destructive"
        });
      } else {
        console.log('[useUserIdentities] Link request successful, redirecting to Google...');
      }
      // If successful, the user will be redirected to Google OAuth
    } catch (error) {
      sessionStorage.removeItem('linking_google_account');
      console.error('[useUserIdentities] Unexpected error linking Google account:', error);
      toast({
        title: "Failed to link account",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLinking(false);
    }
  };

  const unlinkAccount = async (identity: UserIdentity) => {
    // Check if user has at least 2 identities
    if (identities.length <= 1) {
      toast({
        title: "Cannot unlink account",
        description: "You must have at least one linked account to sign in",
        variant: "destructive"
      });
      return;
    }
    
    setUnlinking(identity.id);
    
    try {
      console.log('[useUserIdentities] Unlinking identity:', identity);
      const { error } = await unlinkIdentity(identity);
      
      if (error) {
        toast({
          title: "Failed to unlink account",
          description: error.message,
          variant: "destructive"
        });
        return;
      }
      
      toast({
        title: "Account unlinked",
        description: "The Google account has been successfully unlinked",
      });
      
      // Refresh identities list
      await fetchIdentities();
    } catch (error) {
      console.error('[useUserIdentities] Error unlinking account:', error);
      toast({
        title: "Failed to unlink account",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setUnlinking(null);
    }
  };

  // Check if we're returning from a linking flow
  const checkLinkingStatus = useCallback(() => {
    const wasLinking = sessionStorage.getItem('linking_google_account');
    if (wasLinking) {
      sessionStorage.removeItem('linking_google_account');
      setLinking(false);
      // The ProfileManagement component will handle the success/error toast
      // based on URL params
    }
  }, []);

  return {
    identities,
    loading,
    linking,
    unlinking,
    linkNewGoogleAccount,
    unlinkAccount,
    refetchIdentities: fetchIdentities,
    checkLinkingStatus,
    canUnlink: identities.length > 1
  };
}